import { useMemo, useState, type FormEvent } from 'react'
import { Alert, Box, Button, Card, CardContent, Link as MuiLink, Stack, TextField, Typography } from '@mui/material'
import ShieldRounded from '@mui/icons-material/ShieldRounded'
import { api } from '../lib/api'
import { friendlyMessage } from '../lib/errors'
import { useAuth } from '../auth/AuthProvider'
import { BrandBackground } from '../components/BrandBackground'
import { PublicHeader } from '../components/PublicHeader'
import { AppFooter } from '../components/AppFooter'

const BRAND = '#2563eb'
const BRAND_ACCENT = '#0ea5e9'

// Mesma política do backend (TemporaryPassword.validateChosen) -- valida no cliente pra
// dar feedback imediato; a checagem de verdade continua no servidor.
function policyError(pw: string, email: string): string | null {
  if (pw.length < 12) return 'A senha precisa ter pelo menos 12 caracteres.'
  if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) return 'A senha precisa ter ao menos uma letra e um número.'
  const localPart = email.split('@')[0]?.toLowerCase()
  if (localPart && pw.toLowerCase().includes(localPart)) return 'A senha não pode conter seu e-mail.'
  return null
}

export function ChangePasswordPage() {
  const auth = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const localError = useMemo(() => {
    if (!password) return null
    const pe = policyError(password, auth.profile.email)
    if (pe) return pe
    if (confirm && confirm !== password) return 'As senhas não conferem.'
    return null
  }, [password, confirm, auth.profile.email])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (localError || confirm !== password) { setError(localError ?? 'As senhas não conferem.'); return }
    setError(null)
    setSubmitting(true)
    try {
      await api.changeOwnPassword(password, auth.token)
      auth.clearMustChangePassword()
    } catch (err) {
      setError(friendlyMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <BrandBackground />
      <PublicHeader action={<Button size="small" onClick={() => auth.logout()}>Sair</Button>} />
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 460, boxShadow: '0 28px 64px -24px rgba(15,23,42,.28), 0 0 0 1px rgba(15,23,42,.04)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Stack alignItems="center" textAlign="center">
            <Stack sx={{ width: 88, height: 88, borderRadius: 4, display: 'grid', placeItems: 'center', color: 'white', background: `linear-gradient(135deg,${BRAND},${BRAND_ACCENT})`, boxShadow: '0 16px 34px -14px rgba(37,99,235,.55)', mb: 2 }}>
              <ShieldRounded sx={{ fontSize: 40 }} />
            </Stack>
            <Typography variant="h4">Defina sua nova senha</Typography>
            <Typography color="text.secondary" mt={1} mb={3}>
              Você entrou com uma senha temporária. Escolha uma senha nova para continuar.
            </Typography>
          </Stack>
          <Stack component="form" onSubmit={handleSubmit} gap={2}>
            <TextField label="Nova senha" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password" autoFocus required fullWidth
              helperText="Mínimo 12 caracteres, com ao menos uma letra e um número." />
            <TextField label="Confirmar nova senha" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password" required fullWidth
              error={Boolean(confirm) && confirm !== password}
              helperText={Boolean(confirm) && confirm !== password ? 'As senhas não conferem.' : ' '} />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" fullWidth size="large" variant="contained" loading={submitting}
              disabled={!password || !confirm || Boolean(localError)}
              sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#1d4ed8' } }}>
              Salvar e continuar
            </Button>
            <Typography variant="body2" textAlign="center">
              <MuiLink component="button" type="button" onClick={() => auth.logout()} sx={{ color: 'text.secondary' }} underline="hover">
                Sair
              </MuiLink>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
      </Box>
      <AppFooter />
    </Box>
  )
}
